import { useForm as createUseForm } from "@ant-design-vue/use";
import { reactive, toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../app.logic";
import { createFieldInput, createFieldTextArea } from "../../../components/input/template-builder/template-builder.logic";
import { setAppPageHeader } from "../../../components/layout/app-layout/app-layout.logic";
import { createColumnDatetime, createColumnInput, createColumnTextTruncate } from "../../../components/table/c-table.logic";
import i18n from "../../../i18n";
import store from "../../../store";
import { checkAttribute, initUseForm, onClickEdit, onClickView } from "../../../utils/form";
import { createLoading } from "../../../utils/loading";
import { createAppPageHeaderTop, createManageExportCsv, createPage, createRowActionView } from "../../../utils/page";
import { json, validateError } from "../../../utils/validate";
import lodash from "lodash";

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/security/securityEvent";
export const routerPrefix = "security-events";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });

export const onSearch = ({ page, isSet = true }) => {
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], sort: [{ name: "updateDate", type: "DESC" }] } })
        .then(page => (isSet ? (state.page = { ...page }) : page))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: data.useForm.modelRef })
                .finally(() => setIsLoadingApp(false))
                .then(record => onClickView({ record, routerPrefix }));
        })
        .catch(error => validateError({ error }));
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["*"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createColumns = () => {
    const columns = [
        createColumnInput({ title: () => i18n.global.t("model.securityEvent.event"), dataIndex: "event" }),
        createColumnInput({ title: () => i18n.global.t("model.securityEvent.ip"), dataIndex: "ip" }),
        createColumnTextTruncate({ title: () => i18n.global.t("model.securityEvent.attribute"), dataIndex: "attribute", width: 200 }),
        createColumnTextTruncate({ title: () => i18n.global.t("model.securityEvent.error"), dataIndex: "error", width: 200 }),
        createColumnDatetime({ title: () => i18n.global.t("model.securityEvent.createDate"), dataIndex: "createDate" })
    ];
    return columns;
};

export const createForm = () => {
    return {
        event: null,
        ip: null,
        attribute: null,
        attributeStr: null,
        error: null
    };
};

export const createRules = () => {
    return {
        attributeStr: [json]
    };
};

export const createField = ({ props, data }) => {
    const viewOnly = props.viewOnly;
    const useForm = data.useForm;
    const modelRef = useForm.modelRef;
    return [
        createFieldInput({
            key: "event",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.securityEvent.event"),
            inputProp: () => ({ value: modelRef.event, "onUpdate:value": value => (modelRef.event = value), disabled: viewOnly })
        }),
        createFieldInput({
            key: "ip",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.securityEvent.ip"),
            inputProp: () => ({ value: modelRef.ip, "onUpdate:value": value => (modelRef.ip = value), disabled: viewOnly })
        }),
        createFieldTextArea({
            key: "attributeStr",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.securityEvent.attribute"),
            inputProp: () => {
                const onBlur = () => {
                    try {
                        modelRef.attribute = JSON.parse(modelRef.attributeStr);
                        modelRef.attributeStr = JSON.stringify(modelRef.attribute, null, 4);
                    } catch (error) {
                        error;
                    }
                    useForm.validate("attributeStr").catch(() => {});
                };
                return {
                    value: modelRef.attributeStr,
                    "onUpdate:value": value => (modelRef.attributeStr = value),
                    rows: 6,
                    autoSize: false,
                    onBlur,
                    disabled: viewOnly
                };
            }
        }),
        createFieldTextArea({
            key: "error",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.securityEvent.error"),
            inputProp: () => ({ value: modelRef.error, "onUpdate:value": value => (modelRef.error = value), disabled: viewOnly, rows: 6, autoSize: false })
        })
    ];
};

export const createFormTitle = ({ data }) => {
    return data.useForm.modelRef.id ? data.useForm.modelRef.event : i18n.global.t("button.create");
};

export const loadForm = ({ props, data }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => {
            initUseForm({ data, page, routerPrefix });
            modelRef.attributeStr = page.data[0]?.attribute ? JSON.stringify(page.data[0]?.attribute, null, 4) : "";
        })
        .finally(() => setIsLoading(false));
};

export const initState = ({ props }) => {
    const menuContext = props.menuContext;
    state.manages = [createManageExportCsv({ menuContext, callData: ({ page }) => onSearch({ props, page: { ...lodash.cloneDeep({ ...page, page: 0, size: 1000000 }) }, isSet: false }) })];
    state.manageExtras = [];
    state.rowActions = [createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) })];
};

export const initForm = ({ props, data, refs }) => {
    checkAttribute({ props, data, refs });
    data.form = createForm();
    data.rules = createRules();
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit })
                }
            });
        }
    });
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({ options: { select: ["*"] } }),
    tableOptions: { search: { activeKey: "Search" } }
});
